import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiTag } from 'react-icons/fi';
import productService from '../../services/product.service';
import { useLanguage } from '../../context/LanguageContext';

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ products: [], categories: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const { t } = useLanguage();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < 2) {
      setResults({ products: [], categories: [] });
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await productService.searchAutocomplete(value);
        setResults(data.data);
        setOpen(true);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const goToProduct = (slug) => {
    setOpen(false);
    setQuery('');
    navigate(`/products/${slug}`);
  };

  const goToCategory = (category) => {
    setOpen(false);
    setQuery('');
    navigate(`/products?category=${encodeURIComponent(category)}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setOpen(false);
      navigate(`/products?search=${encodeURIComponent(query.trim())}`);
      setQuery('');
    }
  };

  const hasResults = results.products.length > 0 || results.categories.length > 0;

  return (
    <div ref={wrapperRef} className="position-relative" style={{ width: '100%', maxWidth: '420px' }}>
      <form onSubmit={handleSubmit} className="d-flex align-items-center">
        <div className="input-group input-group-sm">
          <span className="input-group-text border-end-0" style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.25)' }}>
            <FiSearch size={14} style={{ color: 'rgba(255,255,255,0.7)' }} />
          </span>
          <input
            type="text"
            className="form-control border-start-0 shadow-none"
            placeholder={t('searchProducts')}
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => query.length >= 2 && hasResults && setOpen(true)}
            style={{ borderColor: '#E5E7EB', fontSize: '0.875rem' }}
          />
          {query && (
            <button
              type="button"
              className="input-group-text border-start-0"
              style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.25)', cursor: 'pointer' }}
              onClick={() => { setQuery(''); setOpen(false); }}
            >
              <FiX size={14} className="text-muted" />
            </button>
          )}
        </div>
      </form>

      {/* Dropdown */}
      {open && (
        <div
          className="position-absolute w-100 bg-white rounded-3 shadow-lg border mt-1"
          style={{ zIndex: 1050, maxHeight: '400px', overflowY: 'auto' }}
        >
          {loading && (
            <div className="p-3 text-center text-muted small">{t('loading')}</div>
          )}

          {!loading && !hasResults && query.length >= 2 && (
            <div className="p-3 text-center text-muted small">{t('noResults')}</div>
          )}

          {/* Categories */}
          {results.categories.length > 0 && (
            <div className="px-3 pt-2 pb-1">
              <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.7rem' }}>
                {t('categories')}
              </small>
              {results.categories.map((cat, i) => (
                <button
                  key={i}
                  className="d-flex align-items-center gap-2 w-100 text-start border-0 bg-transparent py-1 px-1 rounded text-dark"
                  onClick={() => goToCategory(cat)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <FiTag size={13} className="text-muted" />
                  <span className="small">{cat}</span>
                </button>
              ))}
            </div>
          )}

          {/* Products */}
          {results.products.length > 0 && (
            <div className="px-3 pt-2 pb-2">
              {results.categories.length > 0 && <hr className="my-1" />}
              <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.7rem' }}>
                {t('products')}
              </small>
              {results.products.map((product) => {
                const img = product.images?.find(i => i.isPrimary) || product.images?.[0];
                return (
                  <button
                    key={product._id}
                    className="d-flex align-items-center gap-2 w-100 text-start border-0 bg-transparent py-2 px-1 rounded"
                    onClick={() => goToProduct(product.slug)}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {img?.url ? (
                      <img
                        src={img.url} alt={product.name}
                        style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#F3F4F6', flexShrink: 0 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div className="small text-dark text-truncate" style={{ maxWidth: '280px' }}>
                        {product.name}
                      </div>
                      <div className="text-primary fw-semibold" style={{ fontSize: '0.8rem' }}>
                        ${product.price?.toFixed(2)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* View all */}
          {hasResults && (
            <div className="border-top px-3 py-2">
              <button
                className="btn btn-sm btn-fd-primary w-100"
                onClick={handleSubmit}
              >
                {t('viewAllResultsFor')} "{query}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
